import { useCallback, useEffect, useMemo, useState } from 'react'
import MetaTags from '../components/common/MetaTags'
import ResumeUploadCard from '../components/common/ResumeUploadCard'
import { useProfileContext } from '../components/layout/AuthenticatedLayout'
import { DEFAULT_THEME, GOOGLE_FONTS, THEME_PRESETS } from '../constants/theme'
import { useGoogleFont } from '../hooks/useGoogleFont'
import { authService } from '../services/authService'
import { profileService } from '../services/profileService'

export default function CustomizePage() {
  const { profile, setProfile } = useProfileContext()
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_THEME.background)
  const [accentColor, setAccentColor] = useState(DEFAULT_THEME.accent)
  const [font, setFont] = useState(DEFAULT_THEME.font)
  const [isSaving, setIsSaving] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Load Google Font
  useGoogleFont(font)

  // Initialize from profile
  useEffect(() => {
    if (profile) {
      setBackgroundColor(profile.background_color || DEFAULT_THEME.background)
      setAccentColor(profile.accent_color || DEFAULT_THEME.accent)
      setFont(profile.theme_font || DEFAULT_THEME.font)
    }
  }, [profile])

  // Build preview URL with auth tokens
  const previewUrl = useMemo(() => {
    if (!profile) return ''

    const tokens = authService.getStoredTokens()
    if (!tokens) return ''

    const baseUrl = `/api/v1/profile/preview`
    const params = new URLSearchParams({
      uid: tokens.uid,
      client: tokens.client,
      access_token: tokens.accessToken,
      background: backgroundColor,
      accent: accentColor,
      font: font,
    })

    return `${baseUrl}?${params.toString()}`
  }, [profile, backgroundColor, accentColor, font])

  const handlePresetClick = useCallback((preset: (typeof THEME_PRESETS)[0]) => {
    setBackgroundColor(preset.background)
    setAccentColor(preset.accent)
  }, [])

  const handleSave = async () => {
    if (!profile) return

    setIsSaving(true)
    try {
      const updatedProfile = await profileService.updateProfile({
        background_color: backgroundColor,
        accent_color: accentColor,
        theme_font: font,
      })
      setProfile(updatedProfile)
      setSuccessMessage('Theme saved!')
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error) {
      console.error('Failed to save theme:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const hasChanges = useMemo(() => {
    if (!profile) return false
    return (
      backgroundColor !== (profile.background_color || DEFAULT_THEME.background) ||
      accentColor !== (profile.accent_color || DEFAULT_THEME.accent) ||
      font !== (profile.theme_font || DEFAULT_THEME.font)
    )
  }, [profile, backgroundColor, accentColor, font])

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto">
        <MetaTags
          title="Customize | profile.work"
          description="Customize your profile theme and colors."
        />
        <ResumeUploadCard
          title="No profile to customize"
          subtitle="Upload your resume to create your professional profile first."
          icon={
            <svg
              className="w-8 h-8 text-black/40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
              />
            </svg>
          }
        />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      <MetaTags
        title="Customize | profile.work"
        description="Customize your profile theme and colors."
      />

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2">
          <div className="px-4 py-3 bg-black/90 backdrop-blur-xl rounded-xl shadow-lg">
            <p className="text-sm text-white flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {successMessage}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1
          className="text-2xl sm:text-3xl text-black mb-2"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          Customize
        </h1>
        <p className="text-black/60">
          Personalize your profile's appearance with colors and fonts.
        </p>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Settings Panel */}
        <div className="lg:w-80 flex-shrink-0">
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-6 space-y-6">
            {/* Presets */}
            <div>
              <label className="block text-sm font-medium text-black mb-3">Presets</label>
              <div className="flex flex-wrap gap-2">
                {THEME_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => handlePresetClick(preset)}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      backgroundColor === preset.background && accentColor === preset.accent
                        ? 'border-black scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{
                      background:
                        preset.background === '#ffffff'
                          ? preset.accent
                          : `linear-gradient(135deg, ${preset.background} 50%, ${preset.accent} 50%)`,
                    }}
                    title={preset.name}
                  />
                ))}
              </div>
            </div>

            {/* Background Color */}
            <div>
              <label className="block text-sm font-medium text-black mb-2">Background</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  className="flex-1 px-3 py-2 bg-white border border-black/20 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-black/20"
                  placeholder="#ffffff"
                />
                <input
                  type="color"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-black/20 cursor-pointer"
                />
              </div>
            </div>

            {/* Accent Color */}
            <div>
              <label className="block text-sm font-medium text-black mb-2">Accent Color</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="flex-1 px-3 py-2 bg-white border border-black/20 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-black/20"
                  placeholder="#111827"
                />
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-black/20 cursor-pointer"
                />
              </div>
              <p className="text-xs text-black/50 mt-1">Used for links and interactive elements</p>
            </div>

            {/* Font */}
            <div>
              <label className="block text-sm font-medium text-black mb-2">Font</label>
              <select
                value={font}
                onChange={(e) => setFont(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-black/20 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/20"
              >
                {GOOGLE_FONTS.map((fontName) => (
                  <option key={fontName} value={fontName}>
                    {fontName}
                  </option>
                ))}
              </select>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="w-full py-3 px-4 bg-black text-white font-medium rounded-xl hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="flex-1 min-w-0">
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
            <div className="p-4 border-b border-black/10 flex items-center justify-between">
              <span className="text-sm font-medium text-black">Preview</span>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
            </div>
            <div className="relative" style={{ height: '600px' }}>
              {previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="absolute inset-0 w-full h-full border-0"
                  title="Profile Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-black/40">
                  <p>Loading preview...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
